import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserProfile, selectUserDetails, selectUserLoading, selectUserError, clearUserProfile } from '../store/slices/userSlice';
import storageHelper from '../utils/storageHelper';

const useUserProfile = () => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails);
  const isLoading = useSelector(selectUserLoading);
  const error = useSelector(selectUserError);
  const [authToken, setAuthToken] = useState(storageHelper.get('authToken'));

  // Listen for localStorage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'authToken') {
        const newToken = e.newValue;
        setAuthToken(newToken);

        if (!newToken) {
          // Clear user profile when token is removed
          dispatch(clearUserProfile());
        } else if (newToken && !userDetails.email) {
          // Fetch profile when new token is added
          dispatch(fetchUserProfile());
        }
      }
    };

    // Listen for storage events (works across tabs)
    window.addEventListener('storage', handleStorageChange);

    // Create a MutationObserver to watch for direct localStorage changes
    const observer = new MutationObserver(() => {
      const currentToken = storageHelper.get('authToken');
      if (currentToken !== authToken) {
        setAuthToken(currentToken);

        if (!currentToken) {
          dispatch(clearUserProfile());
        } else if (currentToken && !userDetails.email) {
          dispatch(fetchUserProfile());
        }
      }
    });

    // Observe localStorage changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });

    // Initial fetch if token exists
    if (authToken && !userDetails.email) {
      dispatch(fetchUserProfile());
    }

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      observer.disconnect();
    };
  }, [dispatch, authToken, userDetails.email]);

  // Custom method to handle manual token updates
  const updateAuthToken = (newToken) => {
    if (newToken) {
      // localStorage.setItem('authToken', newToken);
      storageHelper.set('authToken', newToken);
    } else {
      // localStorage.removeItem('authToken');
      storageHelper.remove('authToken');
    }
    setAuthToken(newToken);
  };

  return {
    userDetails,
    isLoading,
    error,
    updateAuthToken,
    isAuthenticated: !!authToken
  };
};

export default useUserProfile;
